import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, Subject, takeUntil, startWith } from 'rxjs';

import { Order } from 'src/app/order-module/shared/entities/order/order';
import { Contact } from 'src/app/rent-module/shared/entities/contact/contact';
import { Resource } from 'src/app/rent-module/shared/entities/resource/resource';
import { Task } from 'src/app/rent-module/shared/entities/task/task';
import { LicenseService } from '../../shared/services/license.service';
import { OrdersSearchService, SearchResults } from './orders-search.service';
import { KeyboardNavigationType } from '../../directives/keyboard-navigation.directive';
import { Issue } from './graphql';

@Component({
  selector: "app-orders-search",
  templateUrl: "./orders-search.component.html",
  host: {
    class: 'min-w-[28rem]'
  },
  styleUrls: ["./orders-search.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrdersSearchComponent implements OnDestroy {
  // Injected services
  private readonly router = inject(Router);
  readonly licenseService = inject(LicenseService);
  private readonly searchService = inject(OrdersSearchService);

  // Lifecycle
  private readonly destroy$ = new Subject<void>();

  // Signals for reactive state
  readonly overlayVisible = signal(false);
  readonly loading = signal(false);
  readonly searchResults = signal<SearchResults>({
    orders: [],
    resources: [],
    customers: [],
    issues: [],
    tasks: [],
    noOrderFound: false,
    noResourceFound: false,
    noCustomerFound: false,
    noIssueFound: false,
    noTaskFound: false
  });

  // Computed signals for derived state
  readonly lastSearchTerms = computed(() => this.searchService.searchHistory());
  readonly orderSearchResult = computed(() => this.searchResults().orders);
  readonly resourcesSearchResult = computed(() => this.searchResults().resources);
  readonly customerSearchResult = computed(() => this.searchResults().customers);
  readonly issueSearchResult = computed(() => this.searchResults().issues);
  readonly taskSearchResult = computed(() => this.searchResults().tasks);
  readonly noOrderFound = computed(() => this.searchResults().noOrderFound);
  readonly noResourceFound = computed(() => this.searchResults().noResourceFound);
  readonly noCustomerFound = computed(() => this.searchResults().noCustomerFound);
  readonly noIssueFound = computed(() => this.searchResults().noIssueFound);
  readonly noTaskFound = computed(() => this.searchResults().noTaskFound);
  
  // Form control
  searchTerm = new FormControl('');
  
  // Convert FormControl value to Signal for reactive template updates
  private readonly searchTermValue = toSignal(
    this.searchTerm.valueChanges.pipe(startWith('')),
    { initialValue: '' }
  );
  readonly hasSearchTerm = computed(() => !!this.searchTermValue());

  // View children
  @ViewChildren('resultItem') resultItems!: QueryList<ElementRef>;
  @ViewChild('searchField', { static: true }) searchField!: ElementRef;

  constructor() {
    this.setupSearchSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubscription(): void {
    this.searchTerm.valueChanges
      .pipe(
        debounceTime(400),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        if (!searchTerm || searchTerm.length < 1) {
          this.resetSearchResults();
          return;
        }

        this.loading.set(true);
        this.searchService.search(searchTerm)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: results => {
              this.searchResults.set(results);
              this.loading.set(false);
            },
            error: () => {
              this.loading.set(false);
              this.resetSearchResults();
            }
          });
      });
  }

  openOrder(order: Order): void {
    this.cleanUp();
    this.overlayVisible.set(false);
    this.navigateTo(`orders/order/${order.id}`);
  }

  openCustomer(customer: Contact): void {
    this.cleanUp();
    this.overlayVisible.set(false);
    this.navigateTo(`customers/customer/${customer.id}`);
  }

  openResource(resource: Resource): void {
    this.cleanUp();
    this.overlayVisible.set(false);
    this.navigateTo(`resources/resource/${resource.id}`);
  }

  openIssue(issue: Issue): void {
    this.cleanUp();
    this.overlayVisible.set(false);
    this.navigateTo(`issues/issue/${issue.id}`);
  }

  openTask(task: Task): void {
    this.cleanUp();
    this.overlayVisible.set(false);
    this.navigateTo(`tasks/task/${task.id}`);
  }

  private navigateTo(uri: string): void {
    this.router.navigateByUrl('./', { skipLocationChange: true }).then(() => {
      this.router.navigate([uri]);
    });
  }

  cleanUp(): void {
    this.searchTerm.setValue("");
    this.resetSearchResults();
    setTimeout(() => {
      this.searchField?.nativeElement?.blur();
    });
  }

  reset(): void {
    this.searchTerm.setValue("");
    this.resetSearchResults();
  }

  private resetSearchResults(): void {
    this.searchResults.set({
      orders: [],
      resources: [],
      customers: [],
      issues: [],
      tasks: [],
      noOrderFound: false,
      noResourceFound: false,
      noCustomerFound: false,
      noIssueFound: false,
      noTaskFound: false
    });
  }

  search(term: string): void {
    this.searchTerm.setValue(term);
  }

  getPlatformKeyIcon(): string {
    return navigator.platform.indexOf('Mac') > -1 ? '⌘' : 'ctrl';
  }

  onKeyDown(event: KeyboardEvent, item: any, type: KeyboardNavigationType): void {
    const elements = this.resultItems.toArray();
    
    switch(event.key) {
      case 'Enter':
        event.preventDefault();
        this.handleEnterKey(item, type);
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.navigateDown(elements, event.target);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.navigateUp(elements, event.target);
        break;
    }
  }

  private handleEnterKey(item: any, type: KeyboardNavigationType): void {
    switch (type) {
      case 'search':
        this.search(item);
        this.focusInputAtEnd();
        break;
      case 'order':
        this.openOrder(item);
        break;
      case 'resource':
        this.openResource(item);
        break;
      case 'customer':
        this.openCustomer(item);
        break;
      case 'issue':
        this.openIssue(item);
        break;
      case 'task':
        this.openTask(item);
        break;
    }
  }

  private navigateDown(elements: ElementRef[], target: EventTarget | null): void {
    let nextIndex = 0;
    if (target) {
      const currentIndex = elements.findIndex(el => el.nativeElement === target);
      nextIndex = currentIndex + 1;
    }

    if (nextIndex > elements.length - 1) {
      this.focusInputAtEnd();
    } else {
      elements[nextIndex].nativeElement.focus();
    }
  }

  private navigateUp(elements: ElementRef[], target: EventTarget | null): void {
    let prevIndex = elements.length;
    if (target) {
      const currentIndex = elements.findIndex(el => el.nativeElement === target);
      prevIndex = currentIndex - 1;
    }

    if (prevIndex === -1) {
      this.focusInputAtEnd();
    } else {
      elements[prevIndex].nativeElement.focus();
    }
  }

  onArrowDown(event: Event): void {
    const elements = this.resultItems.toArray();
    if (elements?.length > 0) {
      elements[0].nativeElement.focus();
    }
  }

  onArrowUp(event: Event): void {
    const elements = this.resultItems.toArray();
    if (elements?.length > 0) {
      elements[elements.length - 1].nativeElement.focus();
    }
  }

  focusInputAtEnd(): void {
    const input = this.searchField.nativeElement;
    input.focus();
    setTimeout(() => {
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }, 0);
  }

  openSearchDialog(): void {
    this.overlayVisible.set(true);
  }
}
